export interface CalendarSpan {
  startMinutes: number;
  endMinutes: number;
}

export interface PlacedCalendarSpan<T extends CalendarSpan> {
  item: T;
  lane: number;
  lanes: number;
}

/**
 * Share a diary column between every visible thing occupying the same time.
 *
 * Lessons and explicit schedule blocks are peers here: if a block overlaps a
 * booked lesson, neither is allowed to paint over the other. Instead the
 * overlapping cluster is split into lanes, matching Acuity's treatment of a
 * block-off as a real calendar card rather than background shading.
 *
 * Input order is the final tie-breaker for otherwise identical spans. TimeGrid
 * supplies lessons before blocks, so an exact lesson/block tie keeps the lesson
 * on the left and the blocked-time card beside it.
 */
export function layOutCalendarSpans<T extends CalendarSpan>(
  items: readonly T[],
): PlacedCalendarSpan<T>[] {
  const ordered = items
    .map((item, index) => ({ item, index }))
    .sort(
      (a, b) =>
        a.item.startMinutes - b.item.startMinutes ||
        a.item.endMinutes - b.item.endMinutes ||
        a.index - b.index,
    );

  const placed: PlacedCalendarSpan<T>[] = [];
  const laneEnds: number[] = [];
  let cluster: number[] = [];
  let clusterEnd = -1;

  const closeCluster = () => {
    const lanes = Math.max(
      1,
      ...cluster.map((index) => (placed[index]?.lane ?? 0) + 1),
    );
    for (const index of cluster) {
      const entry = placed[index];
      if (entry) entry.lanes = lanes;
    }
    cluster = [];
    laneEnds.length = 0;
  };

  for (const { item } of ordered) {
    if (item.startMinutes >= clusterEnd && cluster.length > 0) closeCluster();

    let lane = laneEnds.findIndex((end) => end <= item.startMinutes);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = item.endMinutes;

    placed.push({ item, lane, lanes: 1 });
    cluster.push(placed.length - 1);
    clusterEnd = Math.max(clusterEnd, item.endMinutes);
  }

  if (cluster.length > 0) closeCluster();
  return placed;
}
